import {Model} from 'MWC';

const gold = 'gold',
    silver = 'silver',
    rates = 'pawnshop_rates',
    terms = 'terms',
    weight = 'weight',
    metalAlloy = 'metal_alloy',
    gramCost = 'gram_cost',
    timeUnit = 'time_unit',
    realRate = 'real_rate',
    hundredPercent = 100;

export default class extends Model {
    static fields;

    constructor(data, type) {
        super();
        this._initFields(data);
        this.selectedType = type;
        this.weight = '';
        this.loanTerm = 0;
        this.alloy = this.defineInitialAlloy();
        this.creditSum = 0;
        this.requiredPayment = 0;
        this.weightExceedsLimit = false;
        this.overpayment = 0;
    }

    _initFields(data) {
        this.fields = {
            gold: data[gold],
            silver: data[silver]
        };
    }

    changeField(name, value) {
        if (name === weight && isNaN(value)) {
            return false;
        }

        value = value.trim();
        this[name] = value;

        this.calculateIndicators();
    }

    getType() {
        return this.selectedType;
    }

    switchType(type) {
        this.selectedType = type;
    }

    getWeight() {
        return this.weight;
    }

    setWeight(value) {
        this.weight = value;
    }

    getLimit() {
        const alloy = this.getCurrentMetalAlloy(),
            term = this.getCurrentTerm();

        return term === 0
            ? this.getMetalAlloy()[alloy].limits[0]
            : this.getMetalAlloy()[alloy].limits[1];
    }

    getExceededLimitation() {
        return this.weightExceedsLimit;
    }

    setExceededLimitation(boolean) {
        this.weightExceedsLimit = boolean;
    }

    checkIfWeightExceedsLimit() {
        const weight = this.getWeight(),
            limit = this.getLimit(),
            result = weight > limit;

        this.setExceededLimitation(result);
        return result;
    }

    getTerms() {
        const type = this.getType();

        return this.fields[type].terms;
    }

    getCurrentTerm() {
        return +this.loanTerm;
    }

    getCreditTerm() {
        const loanTerm = this.getCurrentTerm(),
            type = this.getType();

        return this.fields[type][terms][loanTerm];
    }

    setCurrentLoanTerm(value) {
        this.loanTerm = +value;
    }

    getMetalAlloy() {
        const type = this.getType();

        return this.fields[type][metalAlloy];
    }

    getCurrentMetalAlloy() {
        return this.alloy;
    }

    setCurrentMetalAlloy(value) {
        this.alloy = value;
    }

    getRate() {
        const term = this.getCurrentTerm(),
            type = this.getType();

        return this.fields[type][rates][term];
    }

    getCreditSum() {
        return this.creditSum;
    }

    setCreditSum(value) {
        this.creditSum = value;
    }

    getRequiredPayment() {
        return this.requiredPayment;
    }

    setRequiredPayment(value) {
        this.requiredPayment = value;
    }

    getOverpayment() {
        return this.overpayment;
    }

    setOverpayment(value) {
        this.overpayment = value;
    }

    defineGramCost() {
        const alloy = this.getCurrentMetalAlloy(),
            term = this.getCurrentTerm();

        return term === 0
            ? this.getMetalAlloy()[alloy][gramCost][0]
            : this.getMetalAlloy()[alloy][gramCost][1];
    }

    calculateIndicators() {
        const limit = this.getLimit(),
            gramCost = this.defineGramCost(),
            rate = this.getRate(),
            loanTerm = this.getCreditTerm();

        let weight = this.getWeight();

        if (this.checkIfWeightExceedsLimit()) {
            weight = limit;
            this.setWeight(limit);
        }

        if (weight > limit) {
            weight = limit;
            this.setWeight(limit);
        }

        const creditSumValue = this.round(weight * gramCost);
        this.setCreditSum(creditSumValue);

        const requiredPaymentValue = this.round(
            creditSumValue + ((creditSumValue * rate) / hundredPercent) * loanTerm
        );
        this.setRequiredPayment(requiredPaymentValue);

        const overpaymentValue = this.round(requiredPaymentValue - creditSumValue);
        this.setOverpayment(overpaymentValue);
    }

    getTimeUnit() {
        const type = this.getType();

        return this.fields[type][timeUnit];
    }

    realInterestRate() {
        const type = this.getType(),
            term = this.getCurrentTerm();

        return this.fields[type][realRate][term];
    }

    mainSumOfCredit() {
        return Number(this.getCreditSum()) + Number(this.getOverpayment());
    }

    resetIndicators() {
        this.setCurrentMetalAlloy(this.defineInitialAlloy());
        this.setCreditSum(0);
        this.setRequiredPayment(0);
        this.setOverpayment(0);
        this.setCurrentLoanTerm(0);
    }

    defineInitialAlloy() {
        for (const item in this.getMetalAlloy()) {
            return item;
        }
    }

    round(value) {
        return Math.round(value * 100) / 100;
    }
}
